import React, {
  ComponentProps, useMemo, useRef, useState
} from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Filters, createFieldDefs } from '@workboard/ui-list-filters';
import {
  Box, Checkbox, FormControlLabel, ThemeProvider, createTheme
} from '@mui/material';
import { mergeThemeOptions, themeOptions } from '@workboard/wobo-material';
import i18n from '../../i18n';
import { graphRequest } from '../meetings/services/graphRequest';
import { ALIGNMENT_FILTER_STATUS } from '../../constants/okr/alignmentFilter.constants';
import { okrDetailConstants } from '../../constants/okr/okrDetails.constants';
import {
  buildTimePeriodInitialCriterion,
  getTimePeriodFieldDef,
} from '../../utils/timePeriodFilter.util';
import {
  DEFAULT_RELATIONSHIPS_FILTER,
  RelationshipsObjectiveFilter,
  RELATIONSHIPS_SOURCE_TYPES,
  selectedTeamId,
  toRelationshipsCsvParams,
  toRelationshipsFilter,
} from './relationshipsFilter.util';

/** search-api relationshipTeams result. */
interface RelationshipTeam {
  id: string;
  name: string;
  type?: 'FUNCTIONAL' | 'DYNAMIC' | null;
}

const TEAMS_QUERY = 'query ($query: String!) { relationshipTeams(query: $query, limit: 20) { id name type } }';

/**
 * Objective Status + Time Period filters for Admin Tools > Integrations Reporting, built from
 * the shared Filters fields used by the OKR alignment filters. Emits search-api's
 * RelationshipsObjectiveFilterInput.
 */
interface IntegrationsReportingFiltersProps {
  onChange: (filter: RelationshipsObjectiveFilter, csvParams: Record<string, string>) => void;
}

type FiltersInitialValues = ComponentProps<typeof Filters>['initialValues'];

const filtersTheme = createTheme(mergeThemeOptions(themeOptions, {}));

// The criteria are fixed, so hide the compact variant's "Add filter" row that follows the list.
// The team type-ahead is full width by default; cap it and let the selected team's chip use it.
const filtersSx = {
  '& [role="list"] + *': { display: 'none' },
  '& .MuiAutocomplete-root': { maxWidth: 480 },
  '& .MuiAutocomplete-root .MuiChip-root.MuiAutocomplete-tag': { maxWidth: 'calc(100% - 90px)' },
};

const IntegrationsReportingFilters = ({ onChange }: IntegrationsReportingFiltersProps) => {
  const { t } = useTranslation();
  // Team types from the type-ahead, so the org tree checkbox is only offered for functional teams.
  const teamTypes = useRef(new Map<string, RelationshipTeam['type']>());
  const lastValues = useRef<unknown>();
  const [teamId, setTeamId] = useState<string>();
  const [includeTeamOrgTree, setIncludeTeamOrgTree] = useState(false);
  // Refs so a debounced Filters onChange from an earlier render sees the current team and checkbox.
  const teamIdRef = useRef<string>();
  const orgTreeRef = useRef(false);
  const canIncludeOrgTree = !!teamId && teamTypes.current.get(teamId) === 'FUNCTIONAL';

  const objectTypeOptions = useMemo(() => {
    const labels = { OBJECTIVE: t('Objective'), KEY_RESULT: t('Key Result') };
    return RELATIONSHIPS_SOURCE_TYPES.map((value) => ({ label: labels[value], value }));
  }, [t]);

  const fieldDefs = useMemo(() => createFieldDefs({
    fields: {
      status: {
        label: t('Objective Status'),
        type: 'multi-select-button',
        operators: ['is'],
        options: async () => [
          { label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN },
          { label: t('Closed'), value: ALIGNMENT_FILTER_STATUS.CLOSED },
        ],
      },
      objectType: {
        label: t('Object Type'),
        type: 'multi-select-button',
        operators: ['is'],
        options: async () => objectTypeOptions,
      },
      // getTimePeriodFieldDef is plain JS, so pin its literal field type for the typed Filters API.
      timePeriod: { ...getTimePeriodFieldDef(t), type: 'time-periods-select' as const },
      team: {
        label: t('Team'),
        type: 'single-select',
        operators: ['is'],
        options: async (term: string, { abortSignal }: { abortSignal?: AbortSignal }) => {
          try {
            const { data } = await graphRequest(
              { query: TEAMS_QUERY, variables: { query: term } },
              abortSignal,
            );
            return (data.relationshipTeams as RelationshipTeam[]).map((team) => {
              teamTypes.current.set(team.id, team.type);
              const teamType = team.type === 'DYNAMIC' ? t('Dynamic') : t('Functional');
              return { label: `${team.name} (${teamType})`, value: team.id };
            });
          } catch {
            return [];
          }
        },
      },
    },
  }), [t, objectTypeOptions]);

  // Default: open and closed objectives, both object types, no date range, "active on" preselected.
  const initialValues = useMemo(() => ({
    group: {
      operator: 'AND',
      criteria: [
        {
          fieldName: 'status',
          fieldType: 'multi-select-button',
          operator: 'is',
          values: [
            { label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN },
            { label: t('Closed'), value: ALIGNMENT_FILTER_STATUS.CLOSED },
          ],
          nonRemovable: true,
        },
        {
          fieldName: 'objectType',
          fieldType: 'multi-select-button',
          operator: 'is',
          values: objectTypeOptions,
          nonRemovable: true,
        },
        buildTimePeriodInitialCriterion({
          startDate: '',
          endDate: '',
          exDate: okrDetailConstants.EX_DATE_ACTIVE_ON,
        }),
        {
          fieldName: 'team',
          fieldType: 'single-select',
          operator: 'is',
          value: null,
          nonRemovable: true,
        },
      ],
    },
  }) as FiltersInitialValues, [t, objectTypeOptions]);

  const lastEmitted = useRef(JSON.stringify(DEFAULT_RELATIONSHIPS_FILTER));
  const emit = (values: unknown) => {
    lastValues.current = values;
    const nextTeamId = selectedTeamId(values as never);
    // A new team starts without its org tree.
    if (nextTeamId !== teamIdRef.current) {
      teamIdRef.current = nextTeamId;
      orgTreeRef.current = false;
      setTeamId(nextTeamId);
      setIncludeTeamOrgTree(false);
    }
    const isFunctional = !!nextTeamId && teamTypes.current.get(nextTeamId) === 'FUNCTIONAL';
    const filter = toRelationshipsFilter(values as never, orgTreeRef.current && isFunctional);
    const key = JSON.stringify(filter);
    if (key === lastEmitted.current) return;
    lastEmitted.current = key;
    onChange(filter, toRelationshipsCsvParams(filter));
  };

  return (
    <ThemeProvider theme={filtersTheme}>
      <Box sx={filtersSx} data-testid="integrations-reporting-filters">
        <Filters
          fieldDefs={fieldDefs}
          initialValues={initialValues}
          onChange={(state) => emit(state.values)}
          onSubmit={(event: { values?: unknown } = {}) => emit(event.values)}
          showFooter={false}
          showHeader={false}
          variant="compact"
        />
      </Box>
      {canIncludeOrgTree && (
        <Box data-testid="integrations-reporting-team-org-tree">
          <FormControlLabel
            data-testid="integrations-reporting-team-org-tree-label"
            control={(
              <Checkbox
                data-testid="integrations-reporting-team-org-tree-checkbox"
                checked={includeTeamOrgTree}
                onChange={(event) => {
                  orgTreeRef.current = event.target.checked;
                  setIncludeTeamOrgTree(event.target.checked);
                  emit(lastValues.current);
                }}
              />
            )}
            label={t('Include all teams in the organizational tree of the team leader')}
          />
        </Box>
      )}
    </ThemeProvider>
  );
};

declare global {
  interface Window {
    RenderIntegrationsReportingFilters?: (
      props: IntegrationsReportingFiltersProps,
      ele?: HTMLElement | null,
    ) => void;
    UnmountIntegrationsReportingFilters?: (ele?: HTMLElement | null) => void;
  }
}

const rootsByElement = new WeakMap<HTMLElement, ReturnType<typeof createRoot>>();

const UnmountIntegrationsReportingFilters = (ele?: HTMLElement | null) => {
  if (!ele) return;
  rootsByElement.get(ele)?.unmount();
  rootsByElement.delete(ele);
};

const RenderIntegrationsReportingFilters = (
  props: IntegrationsReportingFiltersProps,
  ele?: HTMLElement | null,
) => {
  if (!ele) return;
  UnmountIntegrationsReportingFilters(ele);
  const root = createRoot(ele);
  rootsByElement.set(ele, root);
  root.render(
    <I18nextProvider i18n={i18n}>
      <IntegrationsReportingFilters {...props} />
    </I18nextProvider>,
  );
};

window.RenderIntegrationsReportingFilters = RenderIntegrationsReportingFilters;
window.UnmountIntegrationsReportingFilters = UnmountIntegrationsReportingFilters;
